import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from "shared/utils/firebase";
import { Sun, Moon, ArrowLeft, Layers, Zap, Globe } from 'react-feather';
import styles from './Challenges.module.css';
import UserProfileDropdown from 'shared/components/Auth/UserProfileDropdown';

const CHALLENGES_DATA = [
  {
    id: 'button_debouncer',
    title: 'Button Debouncer',
    difficulty: 'Medium',
    pdk: 'sky130_fd_sc_hd',
    timing: '1000ns',
    area: '≤ 420µm²',
    power: '≤ 130µW',
    tags: ['sequential', 'debounce', 'counter', 'pulse-detect'],
    points: 100,
    solves: 1,
    description: 'Design a robust RTL button debouncer module that filters out mechanical switch contact bouncing. The debouncer should assert a clean, glitch-free output signal only after the input button remains stable for a configured duration.',
    designTemplate: `module button_debouncer (
    input  logic clk,
    input  logic rst_n,
    input  logic button_in,
    output logic debounced_out
);

  // Write your button debouncer RTL here

endmodule
`,
    tbTemplate: `module tb_button_debouncer;
    logic clk;
    logic rst_n;
    logic button_in;
    logic debounced_out;

    button_debouncer uut (
        .clk(clk),
        .rst_n(rst_n),
        .button_in(button_in),
        .debounced_out(debounced_out)
    );

    initial begin
        clk = 0;
        forever #5 clk = ~clk;
    end

    initial begin
        rst_n = 0;
        button_in = 0;
        #20 rst_n = 1;
        #10 button_in = 1;
        #100 button_in = 0;
        #10 button_in = 1;
        #5 button_in = 0; // noise glitch
        #50 button_in = 1;
        #500;
        $finish;
    end
endmodule
`
  },
  {
    id: 'adder_16bit',
    title: '16-bit Adder',
    difficulty: 'Medium',
    pdk: 'sky130_fd_sc_hd',
    timing: '5ns',
    area: '≤ 1000µm²',
    power: '≤ 250µW',
    tags: ['arithmetic', 'adder', 'carry', 'combinational'],
    points: 150,
    solves: 4,
    description: 'Implement a high-speed 16-bit binary adder with carry ripple or carry-lookahead architectures. Ensure your design meets the tight 5ns delay target under the SkyWater 130nm PDK constraints.',
    designTemplate: `module adder_16bit (
    input  logic [15:0] a,
    input  logic [15:0] b,
    input  logic        cin,
    output logic [15:0] sum,
    output logic        cout
);

  // Write your 16-bit adder RTL here

endmodule
`,
    tbTemplate: `module tb_adder_16bit;
    logic [15:0] a;
    logic [15:0] b;
    logic        cin;
    logic [15:0] sum;
    logic        cout;

    adder_16bit uut (
        .a(a),
        .b(b),
        .cin(cin),
        .sum(sum),
        .cout(cout)
    );

    initial begin
        a = 16'h0000; b = 16'h0000; cin = 0;
        #10 a = 16'h1234; b = 16'h5678;
        #10 a = 16'hFFFF; b = 16'h0001;
        #10 a = 16'h7FFF; b = 16'h7FFF; cin = 1;
        #10;
        $finish;
    end
endmodule
`
  },
  {
    id: 'vending_machine',
    title: 'Vending Machine FSM',
    difficulty: 'Hard',
    pdk: 'sky130_fd_sc_hd',
    timing: '10ns',
    area: '≤ 3500µm²',
    power: '≤ 500µW',
    tags: ['fsm', 'vending-machine', 'state-machine', 'sequential'],
    points: 200,
    solves: 4,
    description: 'Design a finite state machine (FSM) that controls a vending machine dispensing items and returning correct change. The items cost 30 cents, and the machine accepts nickels (5c), dimes (10c), and quarters (25c).',
    designTemplate: `module vending_machine (
    input  logic clk,
    input  logic rst_n,
    input  logic nickel_in,   // 5 cents
    input  logic dime_in,     // 10 cents
    input  logic quarter_in,  // 25 cents
    output logic dispense,    // Item cost: 30 cents
    output logic [1:0] change // Change bits
);

  // Write your vending machine FSM here

endmodule
`,
    tbTemplate: `module tb_vending_machine;
    logic clk;
    logic rst_n;
    logic nickel_in;
    logic dime_in;
    logic quarter_in;
    logic dispense;
    logic [1:0] change;

    vending_machine uut (
        .clk(clk),
        .rst_n(rst_n),
        .nickel_in(nickel_in),
        .dime_in(dime_in),
        .quarter_in(quarter_in),
        .dispense(dispense),
        .change(change)
    );

    initial begin
        clk = 0;
        forever #5 clk = ~clk;
    end

    initial begin
        rst_n = 0; nickel_in = 0; dime_in = 0; quarter_in = 0;
        #20 rst_n = 1;
        
        // Feed quarter (25c) + nickel (5c) = 30c (should dispense, 0 change)
        #10 quarter_in = 1; #10 quarter_in = 0;
        #20 nickel_in = 1; #10 nickel_in = 0;
        
        // Wait and feed dime (10c) + dime (10c) + dime (10c) = 30c (dispense)
        #40 dime_in = 1; #10 dime_in = 0;
        #10 dime_in = 1; #10 dime_in = 0;
        #10 dime_in = 1; #10 dime_in = 0;
        
        #100;
        $finish;
    end
endmodule
`
  },
  {
    id: 'traffic_light',
    title: 'Traffic Light FSM',
    difficulty: 'Medium',
    pdk: 'sky130_fd_sc_hd',
    timing: '10ns',
    area: '≤ 2800µm²',
    power: '≤ 300µW',
    tags: ['fsm', 'traffic-light', 'counter', 'sequential'],
    points: 150,
    solves: 4,
    description: 'Implement a traffic light controller FSM sequencing light phases (Green -> Yellow -> Red) for North-South and East-West intersections. Transition immediately when sensor asserts cars on secondary road EW.',
    designTemplate: `module traffic_light (
    input  logic clk,
    input  logic rst_n,
    input  logic sensor,       // Car sensor on secondary road
    output logic [2:0] light_ns, // [red, yellow, green]
    output logic [2:0] light_ew  // [red, yellow, green]
);

  // Write your traffic light controller RTL here

endmodule
`,
    tbTemplate: `module tb_traffic_light;
    logic clk;
    logic rst_n;
    logic sensor;
    logic [2:0] light_ns;
    logic [2:0] light_ew;

    traffic_light uut (
        .clk(clk),
        .rst_n(rst_n),
        .sensor(sensor),
        .light_ns(light_ns),
        .light_ew(light_ew)
    );

    initial begin
        clk = 0;
        forever #5 clk = ~clk;
    end

    initial begin
        rst_n = 0; sensor = 0;
        #20 rst_n = 1;
        #100 sensor = 1;
        #100 sensor = 0;
        #500;
        $finish;
    end
endmodule
`
  },
  {
    id: 'countdown_timer',
    title: '4-Channel Countdown Timer',
    difficulty: 'Medium',
    pdk: 'sky130_fd_sc_hd',
    timing: '10ns',
    area: '≤ 2800µm²',
    power: '≤ 300µW',
    tags: ['sequential', 'counter', 'timer', 'power'],
    points: 150,
    solves: 4,
    description: 'Design a 4-channel countdown timer module with independent reload triggers and completion interrupt flags. Incorporate clock gating optimizations to meet power constraints.',
    designTemplate: `module countdown_timer (
    input  logic clk,
    input  logic rst_n,
    input  logic [3:0] load_en,
    input  logic [15:0] load_val [3:0],
    output logic [3:0] timer_done
);

  // Write your 4-channel timer RTL here

endmodule
`,
    tbTemplate: `module tb_countdown_timer;
    logic clk;
    logic rst_n;
    logic [3:0] load_en;
    logic [15:0] load_val [3:0];
    logic [3:0] timer_done;

    countdown_timer uut (
        .clk(clk),
        .rst_n(rst_n),
        .load_en(load_en),
        .load_val(load_val),
        .timer_done(timer_done)
    );

    initial begin
        clk = 0;
        forever #5 clk = ~clk;
    end

    initial begin
        rst_n = 0; load_en = 4'b0000;
        load_val[0] = 16'h0000; load_val[1] = 16'h0000;
        load_val[2] = 16'h0000; load_val[3] = 16'h0000;
        #20 rst_n = 1;
        #10 load_val[0] = 16'd5; load_val[1] = 16'd10; load_en = 4'b0011;
        #10 load_en = 4'b0000;
        #200;
        $finish;
    end
endmodule
`
  }
];

const MOCK_LEADERBOARD = [
  { rank: '🥉', name: '🤖 AI Baseline', score: '49160', solved: 81, rawPts: 19190, isAI: true },
  { rank: 1, name: 'vlsiguru', score: '42500', solved: 70, rawPts: 16800 },
  { rank: 2, name: 'verilog_coder', score: '38900', solved: 62, rawPts: 15100 },
  { rank: 3, name: 'rtl_ninja', score: '31000', solved: 50, rawPts: 12000 },
  { rank: 4, name: 'skywater_user', score: '25400', solved: 41, rawPts: 9800 }
];

const Challenges = ({ userDetails, auth: initialAuth, theme, toggleTheme }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuth);
  
  // Navigation tabs state: 'challenges' | 'leaderboard'
  const [activeTab, setActiveTab] = useState('challenges');

  // Filter states
  const [difficultyFilter, setDifficultyFilter] = useState('All'); // 'All' | 'Easy' | 'Medium' | 'Hard'
  const [statusFilter, setStatusFilter] = useState('All'); // 'All' | 'Unsolved' | 'Solved'

  // Selected challenge modal state
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  // Redirect to login if user logs out
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);
  // Filter logic
  const filteredChallenges = useMemo(() => {
    return CHALLENGES_DATA.filter((challenge) => {
      const matchesDifficulty = 
        difficultyFilter === 'All' || 
        challenge.difficulty.toLowerCase() === difficultyFilter.toLowerCase();
      
      // Since local user serves as unsolved by default:
      const matchesStatus = 
        statusFilter === 'All' ||
        (statusFilter === 'Unsolved'); // all are unsolved mock-wise

      return matchesDifficulty && matchesStatus;
    });
  }, [difficultyFilter, statusFilter]);

  const handleStartChallenge = (challenge) => {
    // Save active challenge and templates in localStorage
    localStorage.setItem('active_challenge', JSON.stringify({
      id: challenge.id,
      title: challenge.title,
      designTemplate: challenge.designTemplate,
      tbTemplate: challenge.tbTemplate
    }));
    navigate('/ide');
  };

  return (
    <div className={`${styles.challengesContainer} ${theme === 'light' ? styles.lightTheme : ''}`}>
      {/* Brand Header */}
      <header className={styles.headerBar}>
        <div className={styles.logoContainer} onClick={() => navigate('/')} title="Back to home">
          <img src="/static_img/weblogo.png" alt="SiliconLogix Logo" className={styles.webLogo} />
        </div>

        <div className={styles.navActions}>
          <button className={styles.themeToggleBtn} onClick={toggleTheme} title="Switch theme">
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          {isAuthenticated && (
            <UserProfileDropdown userDetails={userDetails} styles={styles} />
          )}
        </div>
      </header>

      <main className={styles.mainContent}>
        {/* Back Navigation */}
        <button className={styles.btnNavBack} onClick={() => navigate('/')}>
          <ArrowLeft size={13} />
          <span>Back to Home</span>
        </button>

        {/* Hero Section */}
        <div className={styles.heroSection}>
          <div className={styles.skillBadge}>
            <span className={styles.badgeDot}></span>
            Test your RTL skills
          </div>
          <h1 className={styles.heroTitle}>
            Hardware Design <span className={styles.gradientText}>Challenges</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Implement RTL designs that meet real silicon constraints — timing, area, and power across all process corners.
          </p>
        </div>

        {/* Stats Dashboard Card */}
        <div className={styles.statsCard}>
          <div className={styles.statColumn}>
            <span className={styles.statNumber}>5</span>
            <span className={styles.statLabel}>CHALLENGES</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statColumn}>
            <span className={styles.statNumber}>0</span>
            <span className={styles.statLabel}>YOUR SOLVES</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statColumn}>
            <span className={styles.statNumber}>0</span>
            <span className={styles.statLabel}>YOUR POINTS</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statColumn}>
            <span className={styles.statNumber} style={{ color: 'var(--text-muted)' }}>-</span>
            <span className={styles.statLabel}>PARTICIPANTS</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className={styles.tabsContainer}>
          <button 
            className={`${styles.tabLink} ${activeTab === 'challenges' ? styles.tabActive : ''}`} 
            onClick={() => setActiveTab('challenges')}
          >
            Challenges
          </button>
          <button 
            className={`${styles.tabLink} ${activeTab === 'leaderboard' ? styles.tabActive : ''}`} 
            onClick={() => setActiveTab('leaderboard')}
          >
            Global Leaderboard
          </button>
        </div>

        {activeTab === 'challenges' ? (
          <>
            {/* Filter Bar */}
            <div className={styles.filterBar}>
              {/* Difficulty Filters */}
              <div className={styles.filterGroup}>
                {['All', 'Easy', 'Medium', 'Hard'].map((diff) => (
                  <button
                    key={diff}
                    className={`${styles.filterPill} ${difficultyFilter === diff ? styles.filterPillActive : ''}`}
                    onClick={() => setDifficultyFilter(diff)}
                  >
                    {diff}
                  </button>
                ))}
              </div>

              {/* Status Filters */}
              <div className={styles.filterGroup}>
                {['All', 'Unsolved', 'Solved'].map((status) => (
                  <button
                    key={status}
                    className={`${styles.filterPill} ${statusFilter === status ? styles.filterPillActive : ''}`}
                    onClick={() => setStatusFilter(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Challenges Grid */}
            <div className={styles.challengesGrid}>
              {filteredChallenges.map((challenge) => (
                <div 
                  key={challenge.id} 
                  className={styles.challengeCard}
                  onClick={() => setSelectedChallenge(challenge)}
                >
                  <div className={styles.cardMain}>
                    <h3 className={styles.cardTitle}>{challenge.title}</h3>
                    
                    {/* Constraints Line */}
                    <div className={styles.cardDetails}>
                      <span 
                        className={styles.difficultyBadge}
                        style={{ 
                          color: challenge.difficulty === 'Easy' ? '#10b981' : challenge.difficulty === 'Medium' ? '#f59e0b' : '#ef4444' 
                        }}
                      >
                        {challenge.difficulty}
                      </span>
                      <span className={styles.detailsDot}>·</span>
                      <span className={styles.detailsText}>{challenge.pdk}</span>
                      <span className={styles.detailsDot}>·</span>
                      <span className={styles.detailsText}>{challenge.timing}</span>
                    </div>

                    {/* Area & Power constraints */}
                    <div className={styles.metricRow}>
                      <div className={styles.metricItem}>
                        <Layers size={13} className={styles.metricIcon} />
                        <span>{challenge.area}</span>
                      </div>
                      <div className={styles.metricItem}>
                        <Zap size={13} className={styles.metricIcon} />
                        <span>{challenge.power}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className={styles.tagsContainer}>
                      {challenge.tags.map((tag) => (
                        <span key={tag} className={styles.tagPill}>{tag}</span>
                      ))}
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className={styles.cardFooter}>
                    <span className={styles.pointsText}>{challenge.points} pts available</span>
                    <span className={styles.solvesCount}>
                      {challenge.solves} {challenge.solves === 1 ? 'solve' : 'solves'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Global Leaderboard Tab Content matching user's photo exactly */
          <div className={styles.leaderboardWrapper}>
            <div className={styles.leaderboardHeaderCard}>
              <div className={styles.leaderboardTitleGroup}>
                <Globe size={18} className={styles.leaderboardGlobeIcon} />
                <span className={styles.leaderboardCardTitle}>Global Rankings</span>
              </div>
              <span className={styles.leaderboardSubtext}>Ranked by difficulty-weighted score</span>
            </div>

            <div className={styles.leaderboardTableContainer}>
              <table className={styles.leaderboardTable}>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>User</th>
                    <th>Score</th>
                    <th>Solved</th>
                    <th>Raw pts</th>
                  </tr>
                </thead>
                <tbody>
                  {MOCK_LEADERBOARD.map((user, idx) => (
                    <tr key={user.name} className={user.isAI ? styles.aiRow : ''}>
                      <td className={styles.rankCell}>{user.rank}</td>
                      <td className={styles.userCell}>
                        {user.isAI ? (
                          <span className={styles.aiNameContainer}>
                            <span role="img" aria-label="robot" style={{ marginRight: '6px' }}>🤖</span>
                            {user.name.replace('🤖 ', '')}
                          </span>
                        ) : (
                          <span>{user.name}</span>
                        )}
                      </td>
                      <td className={styles.scoreCell}>{user.score}</td>
                      <td className={styles.solvedCell}>{user.solved}</td>
                      <td className={styles.rawPtsCell}>{user.rawPts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.leaderboardCaption}>
              Score = sum of best scores weighted by difficulty (Easy×1, Medium×2, Hard×3)
            </div>
          </div>
        )}
      </main>

      {/* Challenge Constraint / Detail Modal */}
      {selectedChallenge && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>{selectedChallenge.title}</h3>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>{selectedChallenge.description}</p>
              
              <div className={styles.modalSectionTitle}>SILICON TARGET CONSTRAINTS</div>
              <div className={styles.constraintsSummaryGrid}>
                <div className={styles.constraintBox}>
                  <span className={styles.boxLabel}>Technology PDK</span>
                  <span className={styles.boxValue}>{selectedChallenge.pdk}</span>
                </div>
                <div className={styles.constraintBox}>
                  <span className={styles.boxLabel}>Timing Target</span>
                  <span className={styles.boxValue}>{selectedChallenge.timing}</span>
                </div>
                <div className={styles.constraintBox}>
                  <span className={styles.boxLabel}>Maximum Area</span>
                  <span className={styles.boxValue}>{selectedChallenge.area}</span>
                </div>
                <div className={styles.constraintBox}>
                  <span className={styles.boxLabel}>Maximum Power</span>
                  <span className={styles.boxValue}>{selectedChallenge.power}</span>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.cancelBtn} 
                onClick={() => setSelectedChallenge(null)}
              >
                Cancel
              </button>
              <button 
                className={styles.confirmBtn}
                onClick={() => handleStartChallenge(selectedChallenge)}
              >
                Start Challenge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Challenges;
